"use client";

import { DataProvider, HttpError, LogicalFilter } from "@refinedev/core";
import { Resources } from "@utils/resources";
import { objectKeys } from "@chakra-ui/utils";
import { getAccessToken, handleAuthError } from "@providers/auth-provider";

const API_URL = process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:3000";

const PAYMENT_REQUIRED_KEY = "bookla.paymentRequiredCompanies";

export const markCompanyPaymentRequired = (companyId: string): void => {
  if (typeof window === "undefined" || !companyId) return;
  try {
    const raw = sessionStorage.getItem(PAYMENT_REQUIRED_KEY);
    const ids: string[] = raw ? JSON.parse(raw) : [];
    if (!ids.includes(companyId)) {
      ids.push(companyId);
      sessionStorage.setItem(PAYMENT_REQUIRED_KEY, JSON.stringify(ids));
    }
  } catch {
    // ignore storage errors
  }
};

export const isCompanyPaymentRequired = (companyId: string): boolean => {
  if (typeof window === "undefined" || !companyId) return false;
  try {
    const raw = sessionStorage.getItem(PAYMENT_REQUIRED_KEY);
    if (!raw) return false;
    const ids: string[] = JSON.parse(raw);
    return ids.includes(companyId);
  } catch {
    return false;
  }
};

export const clearCompanyPaymentRequired = (companyId: string): void => {
  if (typeof window === "undefined" || !companyId) return;
  try {
    const raw = sessionStorage.getItem(PAYMENT_REQUIRED_KEY);
    if (!raw) return;
    const ids: string[] = JSON.parse(raw);
    const next = ids.filter((id) => id !== companyId);
    sessionStorage.setItem(PAYMENT_REQUIRED_KEY, JSON.stringify(next));
  } catch {
    // ignore storage errors
  }
};

const handleError = async (
  response: Response,
  meta?: { company_id?: string },
): Promise<any> => {
  // Handle subscription payment required
  if (response.status === 402) {
    if (meta?.company_id) {
      markCompanyPaymentRequired(meta.company_id);
    }
    const error: HttpError = {
      message: "Subscription payment required",
      statusCode: 402,
    };
    return Promise.reject(error);
  }

  if (response.status === 401) {
    try {
      await handleAuthError();
      // After successful refresh, retry the original request
      const retryResponse = await fetch(response.url);
      if (retryResponse.ok) {
        return retryResponse as any;
      }
    } catch (error) {
      // If refresh failed, handle accordingly
      const anError: HttpError = {
        message: "Authentication failed",
        statusCode: 401,
      };
      return Promise.reject(anError);
    }
  }

  // Error responses are not guaranteed to be JSON. Proxies (nginx, Cloudflare)
  // answer 413/502/504 with an HTML page, and some responses have no body at
  // all, so parsing must never mask the real status code.
  let message = "";
  try {
    const text = await response.text();
    if (text) {
      try {
        message = JSON.parse(text)?.message ?? "";
      } catch {
        // Non-JSON body (HTML error page) - fall back to the status text.
      }
    }
  } catch {
    // Body already consumed or stream failed - fall back to the status text.
  }

  if (!message) {
    message =
      response.status === 413
        ? "Payload too large"
        : response.statusText || `Request failed with status ${response.status}`;
  }

  const error: HttpError = {
    message,
    statusCode: response.status,
  };
  return Promise.reject(error);
};

const uniqueById = <T extends { id: string }>(items: T[]): T[] =>
  Array.from(new Map(items.map((item) => [item.id, item])).values());

// Fetches up to `params.limit` bookings in pages of `meta.batchSize`. The
// first page reports the total, the remaining pages are fetched in parallel.
const getBookingsInBatches = async (
  apiPath: string,
  params: { [k: string]: any },
  meta: { [k: string]: any },
): Promise<any> => {
  const batchSize: number = meta.batchSize;
  const startOffset: number = params.offset ?? 0;
  const maxCount: number = params.limit ?? batchSize;

  const fetchBatch = async (
    offset: number,
    limit: number,
    isRetry = false,
  ): Promise<any> => {
    const query = new URLSearchParams({
      ...params,
      offset: String(offset),
      limit: String(limit),
    });
    const response = await fetch(`${apiPath}?${query.toString()}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getAccessToken()}`,
      },
    });
    // handleError's own 401 retry drops the auth header and resolves with an
    // unparsed Response, so refresh here and retry with the new token.
    // Concurrent refreshes are queued behind one by handleAuthError.
    if (response.status === 401) {
      const authError: HttpError = {
        message: "Authentication failed",
        statusCode: 401,
      };
      if (isRetry) {
        return Promise.reject(authError);
      }
      try {
        await handleAuthError();
      } catch {
        return Promise.reject(authError);
      }
      return fetchBatch(offset, limit, true);
    }
    if (!response.ok) {
      return handleError(response, meta as { company_id?: string });
    }
    return response.json();
  };

  const first = await fetchBatch(startOffset, Math.min(batchSize, maxCount));
  const total: number = first.total ?? 0;
  const end = Math.min(total, startOffset + maxCount);

  const offsets: number[] = [];
  for (let offset = startOffset + batchSize; offset < end; offset += batchSize) {
    offsets.push(offset);
  }
  const rest = await Promise.all(
    offsets.map((offset) =>
      fetchBatch(offset, Math.min(batchSize, end - offset)),
    ),
  );
  const pages = [first, ...rest];

  return {
    // Rows can shift between pages if bookings change mid-load, so the same
    // booking may come back on two pages.
    data: uniqueById(pages.flatMap((page) => page.bookings ?? [])),
    total,
    clients: uniqueById(pages.flatMap((page) => page.clients ?? [])),
    services: uniqueById(pages.flatMap((page) => page.services ?? [])),
    // Slots have no id and may repeat on every page; drop exact duplicates
    // only, since distinct rules can legitimately share a start time.
    slots: Array.from(
      new Map(
        pages
          .flatMap((page) => page.slots ?? [])
          .map((slot) => [JSON.stringify(slot), slot]),
      ).values(),
    ),
    resources: first.resources,
    tickets: uniqueById(pages.flatMap((page) => page.tickets ?? [])),
    blockedTimes: uniqueById(pages.flatMap((page) => page.blockedTimes ?? [])),
  };
};

export const servicesProvider: DataProvider = {
  getList: async ({ resource, pagination, meta, filters }) => {
    let apiPath = `${API_URL}`;
    const token = getAccessToken();
    let method = "GET";
    switch (resource) {
      case Resources.Resources:
      case Resources.Bookings:
      case Resources.Services:
        apiPath += `/companies/${meta?.company_id}/${resource}`;
        if (meta?.grouped) {
          apiPath += `?grouped=true`;
        }
        break;
      case Resources.Clients:
        // Check if we have a query filter for clients
        const hasClientQuery =
          filters &&
          filters.some(
            (f: any) => f.field === "query" || f.field === "clientID",
          );
        if (hasClientQuery) {
          apiPath += `/companies/${meta?.company_id}/${resource}/search`;
        } else {
          apiPath += `/companies/${meta?.company_id}/${resource}`;
        }
        break;
      case Resources.Prices:
      case Resources.Discounts:
      case Resources.Capacity:
      case Resources.TicketAvailability:
      case Resources.Slots:
      case Resources.Tickets:
        apiPath += `/companies/${meta?.company_id}/services/${meta?.id}/${resource}`;
        break;
      case Resources.ServiceSettings:
        apiPath += `/companies/${meta?.company_id}/services/${meta?.service_id}/settings`;
        break;
      case Resources.WorkingHours:
        apiPath += `/companies/${meta?.company_id}/resources/${meta?.id}/working-hours`;
        break;
      case Resources.ClosingPeriods:
        apiPath += `/companies/${meta?.company_id}/resources/${meta?.id}/closing-periods`;
        break;
      case Resources.ClosingPeriodsRange:
        method = "POST";
        apiPath += `/companies/${meta?.company_id}/closing-periods/range`;
        break;
      case Resources.WorkingHoursRange:
        method = "POST";
        apiPath += `/companies/${meta?.company_id}/working-hours/range`;
        break;
      case Resources.APIKeys:
        apiPath += `/api-keys`;
        break;
      case Resources.WebHooks:
        apiPath += `/companies/${meta?.company_id}/events/endpoints`;
        break;
      case Resources.WebHookSubscriptions:
        apiPath += `/companies/${meta?.company_id}/events/endpoints/${meta?.endpointID}/subscriptions`;
        break;
      case Resources.Subscriptions:
        apiPath += `/companies/${meta?.company_id}/plugins/subscription/list`;
        break;
      case Resources.GiftCards:
        apiPath += `/companies/${meta?.company_id}/plugins/giftcards/list`;
        break;
      case Resources.PromoCodes:
        apiPath += `/companies/${meta?.company_id}/plugins/promocodes/list`;
        break;
      case Resources.PromoCodeUsages:
        apiPath += `/companies/${meta?.company_id}/plugins/promocodes/list/${meta?.promo_code_id}/usages`;
        break;
      case Resources.Addons:
        apiPath += `/companies/${meta?.company_id}/plugins/addons`;
        break;
      case Resources.AddonsGroup:
        apiPath += `/companies/${meta?.company_id}/plugins/addons/groups`;
        break;
      case Resources.AddonsServiceLinks:
        apiPath += `/companies/${meta?.company_id}/plugins/addons/services`;
        break;
      case Resources.AddonsSubscriptionLinks:
        apiPath += `/companies/${meta?.company_id}/plugins/addons/subscriptions`;
        break;
      case Resources.GiftCardPurchases:
        method = "POST";
        apiPath += `/companies/${meta?.company_id}/plugins/giftcards/orders/search`;
        break;
      case Resources.GiftCardUsages:
        apiPath += `/companies/${meta?.company_id}/plugins/giftcards/orders/${meta?.purchase_id}/usages`;
        break;
      case Resources.Emails:
        apiPath += `/companies/${meta?.company_id}/plugins/emails/list`;
        break;
      case Resources.EmailReminders:
        apiPath += `/companies/${meta?.company_id}/plugins/emails/reminders`;
        break;
      case Resources.EmailSettings:
        apiPath += `/companies/${meta?.company_id}/plugins/emails/settings`;
        break;
      case Resources.EmailSender:
        apiPath += `/companies/${meta?.company_id}/plugins/emails/settings/sender`;
        break;
      case Resources.SubscriptionPrices:
        apiPath += `/companies/${meta?.company_id}/plugins/subscription/prices`;
        break;
      case Resources.SubscriptionCapacity:
        apiPath += `/companies/${meta?.company_id}/plugins/subscription/capacity`;
        break;
      case Resources.SubscriptionContracts:
        method = "POST";
        apiPath += `/companies/${meta?.company_id}/plugins/subscription/contracts/search`;
        break;
      case Resources.ContractVisitLogs:
        apiPath += `/companies/${meta?.company_id}/plugins/subscription/contracts/${meta?.contract_id}/visits-ledger`;
        break;
      case Resources.CompanyInvite:
        apiPath += `/merchant/invitations`;
        break;
      case Resources.CompanyAccess:
        apiPath += `/merchant/accesses`;
        break;
      case Resources.GoogleCalendarCalendars:
        apiPath += `/companies/${meta?.company_id}/plugins/google-calendar/calendars`;
        break;
      case Resources.Payments:
        apiPath += `/companies/${meta?.company_id}/payments`;
        break;
      case Resources.CompanyRefunds:
        apiPath += `/companies/${meta?.company_id}/refunds`;
        break;
      case Resources.PDFTemplates:
        apiPath += `/companies/${meta?.company_id}/pdf-templates/${meta?.entity_type}`;
        break;
      case Resources.CancellationRules:
        apiPath += `/companies/${meta?.company_id}/plugins/bookingrules/cancellation/list`;
        break;
      case Resources.ReschedulingRules:
        apiPath += `/companies/${meta?.company_id}/plugins/bookingrules/rescheduling/list`;
        break;
      case Resources.ContactDetails:
        apiPath += `/companies/${meta?.company_id}/plugins/bookingrules/contacts`;
        break;
      case Resources.ActivityLogs:
        apiPath += `/companies/${meta?.company_id}/activity`;
        break;
      default:
        apiPath += `/${resource}`;
        break;
    }

    let path = `${apiPath}`;
    let params: { [k: string]: any } = {};
    if (meta && meta.query) {
      params = meta.query;
    }

    if (filters && Object.keys(filters).length > 0) {
      for (const key in filters) {
        const filter = filters[key] as LogicalFilter;
        params[filter.field] = filters[key].value;
      }
    }

    if (pagination) {
      params.offset =
        ((pagination.current ?? 1) - 1) * (pagination.pageSize ?? 0);
      params.limit = meta?.pageSize ?? pagination.pageSize;
    }

    // The bookings endpoint side-loads at most 100 clients per response, so a
    // single large page leaves bookings without their clients. Fetch it in
    // batches small enough for every client to be included, then merge.
    if (resource === Resources.Bookings && meta?.batchSize) {
      return getBookingsInBatches(apiPath, params, meta);
    }

    if (objectKeys(params).length > 0 && method === "GET") {
      path += `?${new URLSearchParams(params).toString()}`;
    }

    const response = await fetch(path, {
      method: method,
      body: method === "POST" ? JSON.stringify(params) : null,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      return handleError(response, meta as { company_id?: string });
    }

    const data = await response.json();

    if (resource === Resources.Bookings) {
      return {
        data: data.bookings,
        total: data.total,
        clients: data.clients,
        services: data.services,
        slots: data.slots,
        resources: data.resources,
        tickets: data.tickets,
        blockedTimes: data.blockedTimes,
      };
    } else if (resource === Resources.Clients) {
      return { data: data.clients, total: data.total };
    } else if (resource === Resources.Slots) {
      return { data: data.slots, total: data.total };
    } else if (
      resource === Resources.WebHooks ||
      resource === Resources.WebHookSubscriptions
    ) {
      return {
        data: data.items ?? [],
        total: data.items ? data.items.length : 0,
      };
    } else if (resource === Resources.SubscriptionContracts) {
      return {
        data: data.items,
        total: data.total,
        clients: data.clients ?? [],
        subscriptions: data.subscriptions ?? [],
      };
    } else if (resource === Resources.GiftCardPurchases) {
      return {
        data: data.items,
        total: data.total,
        clients: data.clients ?? [],
      };
    } else if (resource === Resources.ContractVisitLogs) {
      return { data: data ?? [], total: data ? data.length : 0 };
    } else if (resource === Resources.Payments) {
      return { data: data.transactions, total: data.total };
    } else if (resource === Resources.CompanyRefunds) {
      return { data: data.refunds ?? [], total: data.total ?? 0 };
    } else if (resource === Resources.ClosingPeriodsRange) {
      return { data: data.closingPeriods || {}, total: 0 };
    } else if (resource === Resources.WorkingHoursRange) {
      return { data: data.workingHours || {}, total: 0 };
    } else if (resource === Resources.PDFTemplates) {
      return { data: data || [], total: data ? data.length : 0 };
    } else if (resource === Resources.ActivityLogs) {
      const entries = data.entries ?? [];
      return { data: entries, total: entries.length };
    }

    return { data, total: data ? data.length : 0 };
  },

  create: async ({ resource, variables, meta }) => {
    let apiPath = `${API_URL}`;
    let token = getAccessToken();
    switch (resource) {
      case Resources.Resources:
      case Resources.Services:
      case Resources.Clients:
        apiPath += `/companies/${meta?.company_id}/${resource}`;
        break;
      case Resources.EntityGroups:
        apiPath += `/companies/${meta?.company_id}/entity-groups`;
        break;
      case Resources.ServiceResourceLinks:
      case Resources.Prices:
      case Resources.Discounts:
      case Resources.Capacity:
      case Resources.TicketAvailability:
      case Resources.Slots:
      case Resources.Tickets:
        apiPath += `/companies/${meta?.company_id}/services/${meta?.id}/${resource}`;
        break;
      case Resources.APIKeys:
        apiPath += `/api-keys`;
        break;
      case Resources.Bookings:
        apiPath += `/companies/${meta?.company_id}/${resource}`;
        break;
      case Resources.WorkingHours:
        apiPath += `/companies/${meta?.company_id}/resources/${meta?.id}/working-hours`;
        break;
      case Resources.ClosingPeriods:
        apiPath += `/companies/${meta?.company_id}/resources/${meta?.id}/closing-periods`;
        break;
      case Resources.ResourceCapacity:
        apiPath += `/companies/${meta?.company_id}/resources/${meta?.id}/capacity-rules`;
        break;
      case Resources.ICalFeed:
        apiPath += `/companies/${meta?.company_id}/resources/${meta?.resource_id}/ical-feed/regenerate`;
        break;
      case Resources.WebHooks:
        apiPath += `/companies/${meta?.company_id}/events/endpoints`;
        break;
      case Resources.WebHookSubscriptions:
        apiPath += `/companies/${meta?.company_id}/events/endpoints/${meta?.endpointID}/subscriptions`;
        break;
      case Resources.Subscriptions:
        apiPath += `/companies/${meta?.company_id}/plugins/subscription/list`;
        break;
      case Resources.GiftCards:
        apiPath += `/companies/${meta?.company_id}/plugins/giftcards/list`;
        break;
      case Resources.PromoCodes:
        apiPath += `/companies/${meta?.company_id}/plugins/promocodes/list`;
        break;
      case Resources.GiftCardPurchases:
        apiPath += `/companies/${meta?.company_id}/plugins/giftcards/orders`;
        break;
      case Resources.GiftCardUsages:
        apiPath += `/companies/${meta?.company_id}/plugins/giftcards/orders/${meta?.purchase_id}/usages`;
        break;
      case Resources.Emails:
        apiPath += `/companies/${meta?.company_id}/plugins/emails/list`;
        break;
      case Resources.EmailReminders:
        apiPath += `/companies/${meta?.company_id}/plugins/emails/reminders`;
        break;
      case Resources.EmailSettings:
        apiPath += `/companies/${meta?.company_id}/plugins/emails/settings`;
        break;
      case Resources.EmailSender:
        apiPath += `/companies/${meta?.company_id}/plugins/emails/settings/sender`;
        break;
      case Resources.EmailSenderResend:
        apiPath += `/companies/${meta?.company_id}/plugins/emails/settings/sender/resend`;
        break;
      case Resources.EmailSenderSendTest:
        apiPath += `/companies/${meta?.company_id}/plugins/emails/settings/sender/send-test`;
        break;
      case Resources.Addons:
        apiPath += `/companies/${meta?.company_id}/plugins/addons`;
        break;
      case Resources.AddonsGroup:
        apiPath += `/companies/${meta?.company_id}/plugins/addons/groups`;
        break;
      case Resources.AddonsServiceLinks:
        apiPath += `/companies/${meta?.company_id}/plugins/addons/services/${meta?.serviceId}`;
        break;
      case Resources.AddonsSubscriptionLinks:
        apiPath += `/companies/${meta?.company_id}/plugins/addons/subscriptions/${meta?.subscriptionId}`;
        break;
      case Resources.SubscriptionPrices:
        apiPath += `/companies/${meta?.company_id}/plugins/subscription/prices`;
        break;
      case Resources.SubscriptionCapacity:
        apiPath += `/companies/${meta?.company_id}/plugins/subscription/capacity`;
        break;
      case Resources.SubscriptionContracts:
        apiPath += `/companies/${meta?.company_id}/plugins/subscription/list/${meta?.subscriptionID}/contracts`;
        break;
      case Resources.ContractVisitLogs:
        apiPath += `/companies/${meta?.company_id}/plugins/subscription/contracts/${meta?.contract_id}/visits-ledger`;
        break;
      case Resources.CompanyInvite:
        apiPath += `/merchant/invitations`;
        break;
      case Resources.CompanyInviteAccept:
        apiPath += `/merchant/invitations/accept`;
        break;
      case Resources.PDFTemplates:
        apiPath += `/companies/${meta?.company_id}/pdf-templates/${meta?.entity_type}/${meta?.entity_id}`;
        break;
      case Resources.CancellationRules:
        apiPath += `/companies/${meta?.company_id}/plugins/bookingrules/cancellation/list`;
        break;
      case Resources.ReschedulingRules:
        apiPath += `/companies/${meta?.company_id}/plugins/bookingrules/rescheduling/list`;
        break;
      case Resources.ContactDetails:
        apiPath += `/companies/${meta?.company_id}/plugins/bookingrules/contacts`;
        break;
      case Resources.CodeValidate:
        apiPath += `/companies/${meta?.company_id}/codes/${meta?.code}/validate`;
        break;
      case Resources.BookingResendEmail:
        apiPath += `/companies/${meta?.company_id}/bookings/${meta?.booking_id}/resend-email`;
        break;
      default:
        apiPath += `/${resource}`;
        break;
    }

    let body: any;
    let headers: any = {
      Authorization: `Bearer ${token}`,
    };

    if (resource === Resources.PDFTemplates) {
      const formData = new FormData();
      const vars = variables as any;
      formData.append("file", vars.file);
      formData.append("fields", JSON.stringify(vars.fields));
      body = formData;
    } else {
      body = JSON.stringify(variables);
      headers["Content-Type"] = "application/json";
    }

    const response = await fetch(`${apiPath}`, {
      method: "POST",
      body: body,
      headers: headers,
    });

    if (!response.ok) {
      return handleError(response, meta as { company_id?: string });
    }

    if (response.status < 200 || response.status > 299) throw response;

    if (response.status === 204) return { data: undefined } as any;

    const data = await response.json();

    return { data };
  },

  update: async ({ resource, id, variables, meta }) => {
    let apiPath = `${API_URL}`;
    let method = "PUT";
    let token = getAccessToken();
    switch (resource) {
      case Resources.Companies:
        method = "PATCH";
        apiPath += `/companies/${id}`;
        break;
      case Resources.Resources:
      case Resources.Services:
      case Resources.Bookings:
        method = "PATCH";
        apiPath += `/companies/${meta?.company_id}/${resource}/${id}`;
        break;
      case Resources.EntityGroups:
        method = "PATCH";
        apiPath += `/companies/${meta?.company_id}/entity-groups/${id}`;
        break;
      case Resources.EntityGroupBulk:
        apiPath += `/companies/${meta?.company_id}/entity-groups/structure`;
        method = "PUT";
        break;
      case Resources.ServiceSettings:
        apiPath += `/companies/${meta?.company_id}/services/${id}/settings`;
        method = "PATCH";
        break;
      case Resources.Klix:
      case Resources.Decta:
      case Resources.Stripe:
      case Resources.MercadoPago:
      case Resources.Montonio:
        apiPath += `/companies/${meta?.company_id}/payment/settings`;
        break;
      case Resources.PaymentSettingsPatch:
        method = "PATCH";
        apiPath += `/companies/${meta?.company_id}/payment/settings`;
        break;
      case Resources.PaymentPreferredProvider:
        apiPath += `/companies/${meta?.company_id}/payment/settings/preferred-provider`;
        break;
      case Resources.Plan:
        apiPath += `/plan/change`;
        break;
      case Resources.Subscriptions:
        method = "PATCH";
        apiPath += `/companies/${meta?.company_id}/plugins/subscription/list/${id}`;
        break;
      case Resources.ServiceResourceLinks:
        method = "PATCH";
        apiPath += `/companies/${meta?.company_id}/services/${meta?.service_id}/links/${id}`;
        break;
      case Resources.GiftCards:
        method = "PATCH";
        apiPath += `/companies/${meta?.company_id}/plugins/giftcards/list/${id}`;
        break;
      case Resources.PromoCodes:
        method = "PATCH";
        apiPath += `/companies/${meta?.company_id}/plugins/promocodes/list/${id}`;
        break;
      case Resources.GiftCardUsages:
        method = "PATCH";
        apiPath += `/companies/${meta?.company_id}/plugins/giftcards/orders/${meta?.purchase_id}/usages/${id}`;
        break;
      case Resources.Clients:
        method = "PATCH";
        apiPath += `/companies/${meta?.company_id}/clients/${id}`;
        break;
      case Resources.ScheduleSlots:
        method = "PATCH";
        apiPath += `/companies/${meta?.company_id}/services/${meta?.serviceID}/slots`;
        break;
      case Resources.WorkingHours:
        method = "PATCH";
        apiPath += `/companies/${meta?.company_id}/resources/${meta?.resourceID}/working-hours/${id}`;
        break;
      case Resources.Prices:
        method = "PATCH";
        apiPath += `/companies/${meta?.company_id}/services/${meta?.serviceID}/${resource}/${id}`;
        break;
      case Resources.Emails:
        method = "PATCH";
        apiPath += `/companies/${meta?.company_id}/plugins/emails/list/${id}`;
        break;
      case Resources.EmailReminders:
        method = "PATCH";
        apiPath += `/companies/${meta?.company_id}/plugins/emails/reminders/${id}`;
        break;
      case Resources.EmailSettings:
        method = "PATCH";
        apiPath += `/companies/${meta?.company_id}/plugins/emails/settings`;
        break;
      case Resources.SubscriptionSettings:
        method = "PATCH";
        apiPath += `/companies/${meta?.company_id}/plugins/subscription/settings`;
        break;
      case Resources.SubscriptionPrices:
        method = "PATCH";
        apiPath += `/companies/${meta?.company_id}/plugins/subscription/prices/${id}`;
        break;
      case Resources.Addons:
        apiPath += `/companies/${meta?.company_id}/plugins/addons/${id}`;
        break;
      case Resources.AddonsGroup:
        apiPath += `/companies/${meta?.company_id}/plugins/addons/groups/${id}`;
        break;
      case Resources.AddonsServiceLinks:
        apiPath += `/companies/${meta?.company_id}/plugins/addons/services/${meta?.serviceId}/${id}`;
        break;
      case Resources.AddonsSubscriptionLinks:
        apiPath += `/companies/${meta?.company_id}/plugins/addons/subscriptions/${meta?.subscriptionId}/${id}`;
        break;
      case Resources.SubscriptionCapacity:
        method = "PATCH";
        apiPath += `/companies/${meta?.company_id}/plugins/subscription/capacity/${id}`;
        break;
      case Resources.SubscriptionContracts:
        method = "PATCH";
        apiPath += `/companies/${meta?.company_id}/plugins/subscription/contracts/${id}`;
        break;
      case Resources.EnabledPlugins:
        method = "POST";
        apiPath += `/companies/${meta?.company_id}/plugins/enabled`;
        break;
      case Resources.GoogleCalendarAuth:
        method = "PUT";
        apiPath += `/companies/${meta?.company_id}/plugins/google-calendar/auth`;
        break;
      case Resources.GoogleCalendar:
        method = "PATCH";
        apiPath += `/companies/${meta?.company_id}/plugins/google-calendar/connections`;
        break;
      case Resources.Outseta:
        method = "PUT";
        apiPath += `/companies/${meta?.company_id}/plugins/outseta/info`;
        break;
      case Resources.PDFTemplates:
        method = "PATCH";
        apiPath += `/companies/${meta?.company_id}/pdf-templates/${meta?.entity_type}/${meta?.entity_id}`;
        break;
      case Resources.CancellationRules:
        method = "PATCH";
        apiPath += `/companies/${meta?.company_id}/plugins/bookingrules/cancellation/list/${id}`;
        break;
      case Resources.ReschedulingRules:
        method = "PATCH";
        apiPath += `/companies/${meta?.company_id}/plugins/bookingrules/rescheduling/list/${id}`;
        break;
      case Resources.ContactDetails:
        method = "PATCH";
        apiPath += `/companies/${meta?.company_id}/plugins/bookingrules/contacts`;
        break;
      default:
        apiPath += `/${resource}`;
        break;
    }

    let body: any;
    let headers: any = {
      Authorization: `Bearer ${token}`,
    };

    if (resource === Resources.PDFTemplates) {
      const formData = new FormData();
      const vars = variables as any;
      if (vars.file) {
        formData.append("file", vars.file);
      }
      if (vars.fields) {
        formData.append("fields", JSON.stringify(vars.fields));
      }
      body = formData;
    } else {
      body = JSON.stringify(variables);
      headers["Content-Type"] = "application/json";
    }

    const response = await fetch(`${apiPath}`, {
      method: method,
      body: body,
      headers: headers,
    });

    if (!response.ok) {
      return handleError(response, meta as { company_id?: string });
    }

    if (response.status < 200 || response.status > 299) throw response;

    if (response.status === 204) return { data: undefined };

    const data = await response.json();

    return { data };
  },

  deleteOne: async ({ resource, id, meta }) => {
    let apiPath = `${API_URL}`;
    let token = getAccessToken();
    switch (resource) {
      case Resources.Companies:
        apiPath += `/companies/${id}`;
        break;
      case Resources.Resources:
      case Resources.Services:
      case Resources.Clients:
        apiPath += `/companies/${meta?.company_id}/${resource}/${id}`;
        break;
      case Resources.EntityGroups:
        apiPath += `/companies/${meta?.company_id}/entity-groups/${id}`;
        break;
      case Resources.ServiceResourceLinks:
      case Resources.Tickets:
      case Resources.Capacity:
      case Resources.TicketAvailability:
      case Resources.Prices:
      case Resources.Discounts:
      case Resources.Slots:
        apiPath += `/companies/${meta?.company_id}/services/${meta?.service_id}/${resource}/${id}`;
        break;
      case Resources.ScheduleSlots:
        apiPath += `/companies/${meta?.company_id}/services/${meta?.serviceID}/slots?resourceID=${meta?.resourceID}&startTime=${meta?.startTime}`;
        break;
      case Resources.WorkingHours:
        apiPath += `/companies/${meta?.company_id}/resources/${meta?.resource_id}/working-hours/${id}`;
        break;
      case Resources.ClosingPeriods:
        apiPath += `/companies/${meta?.company_id}/resources/${meta?.resource_id}/closing-periods/${id}`;
        break;
      case Resources.ResourceCapacity:
        apiPath += `/companies/${meta?.company_id}/resources/${meta?.resource_id}/capacity-rules/${id}`;
        break;
      case Resources.ICalFeed:
        apiPath += `/companies/${meta?.company_id}/resources/${meta?.resource_id}/ical-feed`;
        break;
      case Resources.WebHooks:
        apiPath += `/companies/${meta?.company_id}/events/endpoints/${id}`;
        break;
      case Resources.WebHookSubscriptions:
        apiPath += `/companies/${meta?.company_id}/events/endpoints/${meta?.endpoint_id}/subscriptions/${id}`;
        break;
      case Resources.Subscriptions:
        apiPath += `/companies/${meta?.company_id}/plugins/subscription/list/${id}`;
        break;
      case Resources.GiftCards:
        apiPath += `/companies/${meta?.company_id}/plugins/giftcards/list/${id}`;
        break;
      case Resources.GiftCardPurchases:
        apiPath += `/companies/${meta?.company_id}/plugins/giftcards/orders/${id}`;
        break;
      case Resources.PromoCodes:
        apiPath += `/companies/${meta?.company_id}/plugins/promocodes/list/${id}`;
        break;
      case Resources.GiftCardUsages:
        apiPath += `/companies/${meta?.company_id}/plugins/giftcards/orders/${meta?.purchase_id}/usages/${id}`;
        break;
      case Resources.Emails:
        apiPath += `/companies/${meta?.company_id}/plugins/emails/list/${id}`;
        break;
      case Resources.EmailReminders:
        apiPath += `/companies/${meta?.company_id}/plugins/emails/reminders/${id}`;
        break;
      case Resources.EmailSettings:
        apiPath += `/companies/${meta?.company_id}/plugins/emails/settings`;
        break;
      case Resources.EmailSender:
        apiPath += `/companies/${meta?.company_id}/plugins/emails/settings/sender`;
        break;
      case Resources.SubscriptionPrices:
        apiPath += `/companies/${meta?.company_id}/plugins/subscription/prices/${id}`;
        break;
      case Resources.SubscriptionCapacity:
        apiPath += `/companies/${meta?.company_id}/plugins/subscription/capacity/${id}`;
        break;
      case Resources.SubscriptionContracts:
        apiPath += `/companies/${meta?.company_id}/plugins/subscription/contracts/${id}`;
        break;
      case Resources.Addons:
        apiPath += `/companies/${meta?.company_id}/plugins/addons/${id}`;
        break;
      case Resources.AddonsGroup:
        apiPath += `/companies/${meta?.company_id}/plugins/addons/groups/${id}`;
        break;
      case Resources.AddonsServiceLinks:
        apiPath += `/companies/${meta?.company_id}/plugins/addons/services/${meta?.serviceId}/${id}`;
        break;
      case Resources.AddonsSubscriptionLinks:
        apiPath += `/companies/${meta?.company_id}/plugins/addons/subscriptions/${meta?.subscriptionId}/${id}`;
        break;
      case Resources.Bookings:
        apiPath += `/companies/${meta?.company_id}/${resource}/${id}`;
        break;
      case Resources.CompanyAccess:
        apiPath += `/merchant/accesses?merchantID=${meta?.access_merchant_id}&companyID=${meta?.access_company_id}`;
        break;
      case Resources.CompanyInvite:
        apiPath += `/merchant/invitations/${id}`;
        break;
      case Resources.Klix:
      case Resources.Decta:
      case Resources.Stripe:
      case Resources.MercadoPago:
      case Resources.Montonio:
        apiPath += `/companies/${meta?.company_id}/payment/settings/${resource}`;
        break;
      case Resources.GoogleCalendar:
        apiPath += `/companies/${meta?.company_id}/plugins/google-calendar/info`;
        break;
      case Resources.Outseta:
        apiPath += `/companies/${meta?.company_id}/plugins/outseta/info/${id}`;
        break;
      case Resources.APIKeys:
        apiPath += `/api-keys/${id}`;
        break;
      case Resources.PDFTemplates:
        apiPath += `/companies/${meta?.company_id}/pdf-templates/${meta?.entity_type}/${meta?.entity_id}`;
        break;
      case Resources.CancellationRules:
        apiPath += `/companies/${meta?.company_id}/plugins/bookingrules/cancellation/list/${id}`;
        break;
      case Resources.ReschedulingRules:
        apiPath += `/companies/${meta?.company_id}/plugins/bookingrules/rescheduling/list/${id}`;
        break;
      case Resources.ContactDetails:
        apiPath += `/companies/${meta?.company_id}/plugins/bookingrules/contacts?companyID=${meta?.company_id}`;
        break;
      default:
        break;
    }

    const response = await fetch(`${apiPath}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      return handleError(response, meta as { company_id?: string });
    }

    if (response.status < 200 || response.status > 299) throw response;

    return { data: undefined } as any;
  },

  getOne: async ({ resource, id, meta }) => {
    let apiPath = `${API_URL}`;
    let method = "GET";
    let token = getAccessToken();
    let params = {};
    switch (resource) {
      case Resources.Resources:
      case Resources.Services:
        apiPath += `/companies/${meta?.company_id}/${resource}/${id}`;
        break;
      case Resources.GroupedServices:
        apiPath += `/companies/${meta?.company_id}/services`;
        break;
      case Resources.GroupedResources:
        apiPath += `/companies/${meta?.company_id}/resources`;
        break;
      case Resources.ServiceResourceLinks:
      case Resources.Prices:
      case Resources.Discounts:
        apiPath += `/companies/${meta?.company_id}/services/${meta?.id}/${resource}`;
        break;
      case Resources.ResourceServiceLinks:
        apiPath += `/companies/${meta?.company_id}/resources/${meta?.id}/links`;
        break;
      case Resources.Times:
        method = "POST";
        apiPath += `/companies/${meta?.company_id}/services/${meta?.id}/${resource}`;
        params = meta?.requestData;
        break;
      case Resources.Capacity:
        apiPath += `/companies/${meta?.company_id}/services/${meta?.id}/${resource}?type=${meta?.type}`;
        break;
      case Resources.ResourceCapacity:
        apiPath += `/companies/${meta?.company_id}/resources/${meta?.id}/capacity-rules`;
        break;
      case Resources.TicketAvailability:
        apiPath += `/companies/${meta?.company_id}/services/${meta?.id}/${resource}`;
        break;
      case Resources.ServiceSettings:
        apiPath += `/companies/${meta?.company_id}/services/${id}/settings`;
        break;
      case Resources.WorkingHours:
        apiPath += `/companies/${meta?.company_id}/resources/${meta?.id}/working-hours/${id}`;
        break;
      case Resources.ICalFeed:
        apiPath += `/companies/${meta?.company_id}/resources/${meta?.resource_id}/ical-feed`;
        break;
      case Resources.Klix:
      case Resources.Decta:
      case Resources.Stripe:
      case Resources.MercadoPago:
      case Resources.Montonio:
        apiPath += `/companies/${meta?.company_id}/payment/settings`;
        break;
      case Resources.Emails:
        apiPath += `/companies/${meta?.company_id}/plugins/emails/list/${id}`;
        break;
      case Resources.EmailReminders:
        apiPath += `/companies/${meta?.company_id}/plugins/emails/reminders/${id}`;
        break;
      case Resources.EmailSettings:
        apiPath += `/companies/${meta?.company_id}/plugins/emails/settings`;
        break;
      case Resources.EmailSender:
        apiPath += `/companies/${meta?.company_id}/plugins/emails/settings/sender`;
        break;
      case Resources.PromoCodes:
        apiPath += `/companies/${meta?.company_id}/plugins/promocodes/list/${id}`;
        break;
      case Resources.PlanCheckoutURL:
        apiPath += `/plan/payment-url?plan=${id}`;
        break;
      case Resources.SubscriptionSettings:
        apiPath += `/companies/${meta?.company_id}/plugins/subscription/settings`;
        break;
      case Resources.BillingURL:
        apiPath += `/plan/billing-url`;
        break;
      case Resources.EnabledPlugins:
        apiPath += `/companies/${id !== "" ? id : meta?.company_id}/plugins/enabled`;
        break;
      case Resources.Marketplace:
        apiPath += `/companies/${meta?.company_id}/plugins/`;
        break;
      case Resources.GoogleCalendarAuth:
        apiPath += `/companies/${meta?.company_id}/plugins/google-calendar/auth-url`;
        break;
      case Resources.GoogleCalendar:
        apiPath += `/companies/${meta?.company_id}/plugins/google-calendar/info`;
        break;
      case Resources.Bookings:
        apiPath += `/companies/${meta?.company_id}/${resource}/${id}`;
        break;
      case Resources.SubscriptionContracts:
        apiPath += `/companies/${meta?.company_id}/plugins/subscription/contracts/${id}`;
        break;
      case Resources.AddonsServiceLinks:
        apiPath += `/companies/${meta?.company_id}/plugins/addons/services/${id}/links`;
        break;
      case Resources.Outseta:
        apiPath += `/companies/${meta?.company_id}/plugins/outseta/info`;
        break;
      case Resources.CompanyAccessRights:
        apiPath += `/companies/${id}/accesses`;
        break;
      case Resources.PDFTemplates:
        apiPath += `/companies/${meta?.company_id}/pdf-templates/${meta?.entity_type}/${meta?.entity_id}`;
        break;
      case Resources.CancellationRules:
        apiPath += `/companies/${meta?.company_id}/plugins/bookingrules/cancellation/list/${id}`;
        break;
      case Resources.ReschedulingRules:
        apiPath += `/companies/${meta?.company_id}/plugins/bookingrules/rescheduling/list/${id}`;
        break;
      case Resources.ContactDetails:
        apiPath += `/companies/${meta?.company_id}/plugins/bookingrules/contacts?companyID=${meta?.company_id}`;
        break;
      case Resources.Refunds:
        apiPath += `/companies/${meta?.company_id}/refunds/${id}`;
        break;
      default:
        apiPath += `/${resource}`;
        break;
    }

    if (meta && meta.query) {
      params = meta.query;
    }

    let path = apiPath;
    if (objectKeys(params).length > 0 && method === "GET") {
      path += `?${new URLSearchParams(params).toString()}`;
    }

    const response = await fetch(`${path}`, {
      method: method,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: method === "POST" ? JSON.stringify(params) : null,
    });

    if (!response.ok) {
      return handleError(response, meta as { company_id?: string });
    }

    if (response.status < 200 || response.status > 299) throw response;

    const data = await response.json();

    return { data };
  },

  getApiUrl: () => API_URL,
};
